import { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  fetchStorageCredentials,
  fetchStorageOverview,
  ObjectStorageConnectionTestResult,
  ObjectStorageOverview,
  testStorageConnection,
  updateStorageConnection,
} from '../../services/storageApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';

const PROVIDER_OPTIONS = [
  { value: 'Minio', label: 'MinIO' },
  { value: 'AwsS3', label: 'Amazon S3' },
  { value: 'Oracle', label: 'Oracle Object Storage' },
  { value: 'Other', label: 'Outro (S3-compatível)' },
] as const;

function statusLabel(status: string): string {
  switch (status) {
    case 'healthy':
      return 'Saudável';
    case 'unhealthy':
      return 'Com falha';
    case 'notConfigured':
      return 'Não configurado';
    default:
      return status;
  }
}

function statusClassName(status: string): string {
  switch (status) {
    case 'healthy':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200';
    case 'unhealthy':
      return 'bg-rose-50 text-rose-800 border-rose-200';
    case 'notConfigured':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
}

function providerLabel(provider: string): string {
  return PROVIDER_OPTIONS.find((item) => item.value === provider)?.label ?? provider;
}

type FormState = {
  provider: string;
  endpoint: string;
  bucketName: string;
  useSsl: boolean;
  accessKey: string;
  secretKey: string;
};

export function StorageOverviewPanel() {
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('storage');
  const [overview, setOverview] = useState<ObjectStorageOverview | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [credentialsVisible, setCredentialsVisible] = useState(false);
  const [revealedAccessKey, setRevealedAccessKey] = useState<string | null>(null);
  const [revealedSecretKey, setRevealedSecretKey] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<ObjectStorageConnectionTestResult | null>(null);

  const applyOverview = useCallback((data: ObjectStorageOverview): void => {
    setOverview(data);
    setForm({
      provider: data.provider || 'Minio',
      endpoint: data.endpoint || '',
      bucketName: data.bucketName || '',
      useSsl: data.useSsl,
      accessKey: '',
      secretKey: '',
    });
    setCredentialsVisible(false);
    setRevealedAccessKey(null);
    setRevealedSecretKey(null);
  }, []);

  const loadOverview = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      applyOverview(await fetchStorageOverview());
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar a configuração de armazenamento.'));
      setOverview(null);
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [applyOverview]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  async function handleSave(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite || !form) return;

    setSaving(true);
    try {
      const saved = await updateStorageConnection({
        provider: form.provider,
        endpoint: form.endpoint.trim(),
        bucketName: form.bucketName.trim(),
        useSsl: form.useSsl,
        accessKey: form.accessKey.trim() || null,
        secretKey: form.secretKey.trim() || null,
      });
      applyOverview(saved);
      toast.success('Configuração de armazenamento salva.');
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar a configuração.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleTestConnection(): Promise<void> {
    setTesting(true);
    try {
      const result = await testStorageConnection();
      setTestResult(result);
      if (result.succeeded) {
        toast.success('Conexão com o armazenamento OK.');
      } else {
        toast.error(result.message || 'Falha no teste de conexão.');
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao testar a conexão com o armazenamento.'));
    } finally {
      setTesting(false);
    }
  }

  async function handleToggleCredentials(): Promise<void> {
    if (credentialsVisible) {
      setCredentialsVisible(false);
      setRevealedAccessKey(null);
      setRevealedSecretKey(null);
      return;
    }

    if (!canWrite) {
      toast.error('Revelar credenciais exige permissão de escrita em Armazenamento.');
      return;
    }

    setRevealing(true);
    try {
      const credentials = await fetchStorageCredentials();
      setRevealedAccessKey(credentials.accessKey);
      setRevealedSecretKey(credentials.secretKey);
      setCredentialsVisible(true);
    } catch (error) {
      toast.error(
        getRequestErrorMessage(error, 'Não foi possível revelar as credenciais.'),
      );
    } finally {
      setRevealing(false);
    }
  }

  if (loading) {
    return <LoadingSpinner label="Carregando armazenamento..." />;
  }

  if (!overview || !form) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <p className="text-sm text-slate-500">Não foi possível carregar os dados de armazenamento.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <form
        onSubmit={(event) => void handleSave(event)}
        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      >
        <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Conexão de armazenamento</h3>
            <p className="mt-1 text-sm text-slate-500">
              Endpoint S3-compatível usado por anexos, importações e arquivos da plataforma. Os
              objetos ficam isolados sob o prefixo desta empresa.
            </p>
          </div>
          <span
            className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${
              overview.isConfigured
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {overview.isCompanyConfigured
              ? 'Configurado para a empresa'
              : overview.isConfigured
                ? 'Padrão do host'
                : 'Não configurado'}
          </span>
        </div>

        {overview.configurationHint ? (
          <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            {overview.configurationHint}
          </p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Provedor</span>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 disabled:bg-slate-50"
              value={form.provider}
              disabled={!canWrite}
              onChange={(event) => setForm({ ...form, provider: event.target.value })}
            >
              {PROVIDER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-end gap-2 pb-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300"
              checked={form.useSsl}
              disabled={!canWrite}
              onChange={(event) => setForm({ ...form, useSsl: event.target.checked })}
            />
            <span className="text-slate-700">Usar SSL (HTTPS)</span>
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-slate-600">Endpoint</span>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm disabled:bg-slate-50"
              value={form.endpoint}
              disabled={!canWrite}
              onChange={(event) => setForm({ ...form, endpoint: event.target.value })}
              placeholder="storage.exemplo.com:9000"
              required
            />
          </label>

          <label className="block text-sm sm:col-span-2">
            <span className="mb-1 block text-slate-600">Bucket padrão</span>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm disabled:bg-slate-50"
              value={form.bucketName}
              disabled={!canWrite}
              onChange={(event) => setForm({ ...form, bucketName: event.target.value })}
              placeholder="meu-bucket"
              required
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Access Key</span>
            <input
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm disabled:bg-slate-50"
              value={
                credentialsVisible && revealedAccessKey != null
                  ? revealedAccessKey
                  : form.accessKey
              }
              disabled={!canWrite}
              onChange={(event) => {
                setCredentialsVisible(false);
                setForm({ ...form, accessKey: event.target.value });
              }}
              placeholder={
                overview.hasAccessKey
                  ? `Atual: ${overview.accessKeyMasked} — deixe em branco para manter`
                  : 'Access key'
              }
            />
          </label>

          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Secret Key</span>
            <input
              type={credentialsVisible ? 'text' : 'password'}
              className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm disabled:bg-slate-50"
              value={
                credentialsVisible && revealedSecretKey != null
                  ? revealedSecretKey
                  : form.secretKey
              }
              disabled={!canWrite}
              onChange={(event) => {
                setCredentialsVisible(false);
                setForm({ ...form, secretKey: event.target.value });
              }}
              placeholder={
                overview.hasSecretKey
                  ? 'Deixe em branco para manter a secret atual'
                  : 'Secret key'
              }
              autoComplete="new-password"
            />
          </label>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm"
            disabled={testing || !overview.isConfigured}
            onClick={() => void handleTestConnection()}
          >
            {testing ? 'Testando...' : 'Testar conexão'}
          </button>
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm inline-flex items-center gap-1.5"
            disabled={revealing || !overview.hasAccessKey || !canWrite}
            onClick={() => void handleToggleCredentials()}
          >
            {credentialsVisible ? (
              <EyeSlashIcon className="h-4 w-4" aria-hidden />
            ) : (
              <EyeIcon className="h-4 w-4" aria-hidden />
            )}
            {revealing
              ? 'Carregando...'
              : credentialsVisible
                ? 'Ocultar credenciais'
                : 'Mostrar credenciais'}
          </button>
          <button
            type="button"
            className="erp-btn-secondary erp-btn-sm"
            onClick={() => void loadOverview()}
          >
            Recarregar
          </button>
          {canWrite ? (
            <button type="submit" className="erp-btn-primary erp-btn-sm" disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          ) : null}
        </div>

        {!canWrite ? (
          <p className="mt-3 text-xs text-slate-500">
            Edição e revelação de credenciais exigem permissão de escrita em Armazenamento.
          </p>
        ) : (
          <p className="mt-3 text-xs text-slate-500">
            Provedor atual: {providerLabel(form.provider)}. Deixe Access/Secret em branco ao salvar
            para manter as credenciais existentes.
          </p>
        )}
      </form>

      {testResult ? (
        <div className={`rounded-xl border p-4 shadow-sm ${statusClassName(testResult.status)}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold">Resultado do teste</h3>
            <span className="text-xs font-medium">{statusLabel(testResult.status)}</span>
          </div>
          <p className="mt-2 text-sm">{testResult.message}</p>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-xs uppercase tracking-wide opacity-80">Endpoint</dt>
              <dd className="mt-0.5 break-all font-mono">{testResult.endpoint || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide opacity-80">Bucket</dt>
              <dd className="mt-0.5 break-all font-mono">{testResult.bucketName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide opacity-80">Bucket existe</dt>
              <dd className="mt-0.5">
                {testResult.bucketExists == null ? '—' : testResult.bucketExists ? 'Sim' : 'Não'}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wide opacity-80">Latência</dt>
              <dd className="mt-0.5">
                {testResult.latencyMs != null ? `${testResult.latencyMs} ms` : '—'}
              </dd>
            </div>
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide opacity-80">Verificado em</dt>
              <dd className="mt-0.5">{new Date(testResult.checkedAtUtc).toLocaleString('pt-BR')}</dd>
            </div>
          </dl>
        </div>
      ) : null}
    </div>
  );
}
